/**
 * The Pipeline Report's "Filters" toggle + panel — replaces the old per-column
 * filter header row. Trigger convention (label + active-count badge) matches
 * ResourcesFilterBar; panel widened + laid out as a 2-column grid since this
 * table has ~15 filterable fields, far more than the 6-section Resources filter
 * this pattern was built for.
 */

import { SlidersHorizontal } from "lucide-react";
import type { SelectOption } from "@/lib/option-search";
import { FilterPopover } from "./FilterPopover";
import {
  FilterDateRange,
  FilterNumberRange,
  FilterSection,
  FilterSelect,
  FilterText,
} from "./filter-controls";
import { activeFilterCount, type FilterState } from "./filter-state";

export interface PipelineReportFilterOptions {
  resourceOptions: SelectOption[];
  ownerOptions: SelectOption[];
  platformOptions: SelectOption[];
  resourcingStatusOptions: SelectOption[];
  yesNoOptions: SelectOption[];
}

export function PipelineReportFilterBar({
  filters,
  updateFilter,
  onClear,
  options,
}: {
  filters: FilterState;
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  onClear: () => void;
  options: PipelineReportFilterOptions;
}) {
  const count = activeFilterCount(filters);

  return (
    <FilterPopover
      trigger={() => (
        <>
          <SlidersHorizontal className="size-4" />
          <span>Filters{count > 0 ? ` (${count})` : ""}</span>
        </>
      )}
    >
      {(close) => (
        <div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            <FilterSection label="Account">
              <FilterText value={filters.account} onChange={(v) => updateFilter("account", v)} />
            </FilterSection>
            <FilterSection label="Deal">
              <FilterText value={filters.deal} onChange={(v) => updateFilter("deal", v)} />
            </FilterSection>
            <FilterSection label="Stage">
              <FilterText value={filters.stage} onChange={(v) => updateFilter("stage", v)} />
            </FilterSection>
            <FilterSection label="Resource">
              <FilterSelect
                value={filters.resourceId}
                onChange={(v) => updateFilter("resourceId", v)}
                options={options.resourceOptions}
              />
            </FilterSection>
            <FilterSection label="Resourcing Status">
              <FilterSelect
                value={filters.resourcingStatus}
                onChange={(v) => updateFilter("resourcingStatus", v)}
                options={options.resourcingStatusOptions}
                includeNone={false}
              />
            </FilterSection>
            <FilterSection label="Resource Plan Received">
              <FilterSelect
                value={filters.resourcePlanReceived}
                onChange={(v) => updateFilter("resourcePlanReceived", v)}
                options={options.yesNoOptions}
                includeNone={false}
              />
            </FilterSection>
            <FilterSection label="Hours Logged">
              <FilterNumberRange
                min={filters.hoursMin}
                max={filters.hoursMax}
                onMinChange={(v) => updateFilter("hoursMin", v)}
                onMaxChange={(v) => updateFilter("hoursMax", v)}
              />
            </FilterSection>
            <FilterSection label="Notes">
              <FilterText value={filters.notes} onChange={(v) => updateFilter("notes", v)} />
            </FilterSection>
            <FilterSection label="Owner">
              <FilterSelect
                value={filters.ownerId}
                onChange={(v) => updateFilter("ownerId", v)}
                options={options.ownerOptions}
              />
            </FilterSection>
            <FilterSection label="Platform">
              <FilterSelect
                value={filters.platformId}
                onChange={(v) => updateFilter("platformId", v)}
                options={options.platformOptions}
              />
            </FilterSection>
            <FilterSection label="Total">
              <FilterNumberRange
                min={filters.totalMin}
                max={filters.totalMax}
                onMinChange={(v) => updateFilter("totalMin", v)}
                onMaxChange={(v) => updateFilter("totalMax", v)}
              />
            </FilterSection>
            <FilterSection label="Lead Source">
              <FilterText
                value={filters.leadSource}
                onChange={(v) => updateFilter("leadSource", v)}
              />
            </FilterSection>
            <FilterSection label="Commit Date">
              <FilterDateRange
                from={filters.commitFrom}
                to={filters.commitTo}
                onFromChange={(v) => updateFilter("commitFrom", v)}
                onToChange={(v) => updateFilter("commitTo", v)}
              />
            </FilterSection>
            <FilterSection label="Last Activity">
              <FilterDateRange
                from={filters.lastActivityFrom}
                to={filters.lastActivityTo}
                onFromChange={(v) => updateFilter("lastActivityFrom", v)}
                onToChange={(v) => updateFilter("lastActivityTo", v)}
              />
            </FilterSection>
            <FilterSection label="Added">
              <FilterDateRange
                from={filters.addedFrom}
                to={filters.addedTo}
                onFromChange={(v) => updateFilter("addedFrom", v)}
                onToChange={(v) => updateFilter("addedTo", v)}
              />
            </FilterSection>
          </div>

          {count > 0 && (
            <div className="mt-4 flex justify-end border-t border-border pt-3">
              <button
                type="button"
                onClick={() => {
                  onClear();
                  close();
                }}
                className="text-sm text-muted-foreground underline-offset-2 hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}
    </FilterPopover>
  );
}
